import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Clock4, LogOut } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { apiJson } from '@/api/client'
import { logout as logoutApi } from '@/features/auth/api'
import { clearTokens } from '@/lib/auth-tokens'
import { useSessionTimer, formatRemaining } from './useSessionTimer'

/**
 * AF-402 — session timeout warning modal.
 *
 * Opens at T-{warning} minutes; closes when activity bumps the
 * timer back out of the warning window. At T-0 the user is signed
 * out and redirected to /login?reason=timeout.
 *
 * Two CTAs:
 *
 *   - "Stay signed in" — bumps the inactivity timestamp and pings
 *     /me, which exercises the api/client refresh path so a JWT
 *     close to expiry is renewed.
 *   - "Sign out" — clean logout via the existing logout flow.
 *
 * Mounted once at the app shell so it sits above any page state.
 */
export function SessionTimeoutModal() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { phase, remainingMs, bump } = useSessionTimer()
  const expiredHandled = useRef(false)

  // Server-side ping — refreshes the token if close to expiry, and
  // simply confirms we're still authenticated otherwise. We use /me
  // (not a dedicated refresh endpoint) so we don't need to spelunk
  // into the api/client internals.
  const stay = useMutation({
    mutationFn: () => apiJson<{ id: string }>('/me'),
    onSuccess: () => {
      // No-op. The api/client already updates tokens via its
      // refresh path, and bump() reset the inactivity clock before
      // we even fired this.
    },
  })

  function handleStay() {
    bump()
    stay.mutate()
  }

  // Logout flow: clear server-side session, drop tokens, redirect.
  // We use the same shape as useLogout() but inline so we don't
  // depend on its mutation lifecycle here.
  async function handleSignOut(reason?: string) {
    try {
      await logoutApi()
    } catch {
      // Ignore — even if the server call fails we still want to
      // clear local tokens so the user actually exits the session.
    }
    clearTokens()
    queryClient.clear()
    const next = reason ? `/login?reason=${reason}` : '/login'
    void navigate(next, { replace: true })
  }

  // Auto-sign-out at T-0. Guarded by a ref so a stale render after
  // navigation doesn't fire it twice.
  useEffect(() => {
    if (phase === 'expired' && !expiredHandled.current) {
      expiredHandled.current = true
      void handleSignOut('timeout')
    }
    if (phase !== 'expired') {
      expiredHandled.current = false
    }
    // handleSignOut is stable enough for this guard; intentionally
    // omitted from deps to avoid retriggering when navigate identity
    // changes mid-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase])

  const open = phase === 'warning'

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleStay()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock4 className="h-5 w-5 text-amber-500" />
            Your session is about to expire
          </DialogTitle>
          <DialogDescription>
            For security, we sign you out after a period of inactivity. You'll be signed out
            in <span className="font-mono font-semibold">{formatRemaining(remainingMs)}</span>.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-fg">
          Stay signed in to continue working, or sign out now if you're stepping away.
        </p>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleSignOut()}
            disabled={stay.isPending}
          >
            <LogOut className="mr-1.5 h-4 w-4" />
            Sign out
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleStay}
            disabled={stay.isPending}
            // eslint-disable-next-line jsx-a11y/no-autofocus -- modal/dialog focus management; primary control on open
            autoFocus
          >
            {stay.isPending ? 'Extending…' : 'Stay signed in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
